import { useEffect, useMemo, useState } from "react";
import { Alert, Button, Form, Spinner } from "react-bootstrap";
import styled from "styled-components";
import { request } from "hooks/useOkoApi/useOkoApi";
import { logError, LogCategory } from "utils/sentryLogger";
import { colors, constants } from "utils";

const DATASET_PREFIX = constants.STUDENT_FEEDBACK_DATASET_PREFIX;

const Page = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  width: 100%;
  max-width: 100%;
  min-width: 0;
  overflow-x: hidden;
`;

const Controls = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  padding: 16px;
  background: ${colors.white};
  border: 1px solid ${colors.milkGlass400};
  border-radius: 8px;
`;

const ControlField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 240px;
  flex: 1;
`;

const ItemCard = styled.div`
  background: ${colors.white};
  border: 1px solid ${colors.milkGlass400};
  border-radius: 8px;
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  min-width: 0;
  max-width: 100%;
  overflow-x: auto;
  word-break: break-word;
`;

const ItemHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const ItemIndex = styled.span`
  font-weight: 600;
  color: ${colors.blue600 || "#4f46e5"};
`;

const ItemId = styled.span`
  font-family: "SF Mono", Menlo, monospace;
  font-size: 12px;
  color: ${colors.milkGlass600 || "#6b7280"};
`;

const InputDetails = styled.details`
  border: 1px solid ${colors.milkGlass400};
  border-radius: 6px;
  background: ${colors.milkGlass100 || "#fafafa"};
  & > summary {
    cursor: pointer;
    padding: 8px 12px;
    font-size: 13px;
    color: ${colors.milkGlass600 || "#6b7280"};
    user-select: none;
  }
`;

const InputBody = styled.pre`
  padding: 10px 12px;
  font-size: 12px;
  white-space: pre-wrap;
  word-break: break-word;
  margin: 0;
  font-family: "SF Mono", Menlo, monospace;
`;

const ColumnsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px;
`;

const Column = styled.div`
  border: 1px solid ${colors.milkGlass400};
  border-radius: 6px;
  padding: 12px;
  background: #fafafa;
  display: flex;
  flex-direction: column;
  gap: 10px;
  min-width: 0;
`;

const ColumnTitle = styled.div`
  font-weight: 600;
  font-size: 13px;
  color: ${(p) => p.color || "#1f2330"};
  padding-bottom: 6px;
  border-bottom: 1px solid ${colors.milkGlass400};
`;

const FieldLabel = styled.div`
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #6b7280;
  font-weight: 600;
`;

const FieldValue = styled.div`
  font-size: 13px;
  white-space: pre-wrap;
  word-break: break-word;
`;

const PillBadge = styled.span`
  display: inline-block;
  padding: 2px 8px;
  border-radius: 999px;
  background: #eef2ff;
  color: #3730a3;
  font-size: 12px;
  font-weight: 600;
`;

const TagChip = styled.span`
  display: inline-block;
  background: #eef2ff;
  color: #3730a3;
  padding: 2px 8px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 500;
  margin: 2px 4px 2px 0;
  font-family: "SF Mono", Menlo, monospace;
`;

const NoVal = styled.span`
  color: ${colors.milkGlass600 || "#6b7280"};
  font-size: 13px;
`;

const AdditionalDetails = styled.details`
  border: 1px solid ${colors.milkGlass400};
  border-radius: 6px;
  background: #fff;
  & > summary {
    cursor: pointer;
    padding: 6px 10px;
    font-size: 12px;
    color: ${colors.milkGlass600 || "#6b7280"};
    user-select: none;
  }
`;

const AdditionalBody = styled.pre`
  margin: 0;
  padding: 8px 10px;
  font-size: 11px;
  white-space: pre-wrap;
  word-break: break-word;
  font-family: "SF Mono", Menlo, monospace;
`;

const formatLatency = (ms) => {
  if (ms === null || ms === undefined) return "—";
  if (ms < 1000) return `${ms.toFixed(0)} ms`;
  return `${(ms / 1000).toFixed(2)} s`;
};

const shortRunLabel = (runName) => {
  if (!runName) return "";
  const beforeComma = runName.split(",")[0].trim();
  return beforeComma.length > 40 ? `${beforeComma.slice(0, 40)}…` : beforeComma;
};

const EXPECTED_COLOR = "#475569";
const RUN_A_COLOR = "#0ea5e9";
const RUN_B_COLOR = "#d97706";

const SummaryRow = styled.div`
  display: flex;
  gap: 24px;
  flex-wrap: wrap;
  padding: 16px;
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
`;

const SummaryCol = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 220px;
`;

const SummaryTitle = styled.div`
  font-weight: 600;
  font-size: 13px;
  color: ${(p) => p.color || "#1f2330"};
`;

const SummaryStat = styled.div`
  font-size: 13px;
  display: flex;
  justify-content: space-between;
  gap: 12px;
  & > .label { color: #6b7280; }
  & > .val { font-family: "SF Mono", Menlo, monospace; }
`;

const renderSummary = (label, color, summary) => {
  if (!summary) return null;
  return (
    <SummaryCol>
      <SummaryTitle color={color}>{label}</SummaryTitle>
      <SummaryStat><span className="label">Items</span><span className="val">{summary.item_count ?? "—"}</span></SummaryStat>
      <SummaryStat><span className="label">Avg latency</span><span className="val">{formatLatency(summary.avg_latency_ms)}</span></SummaryStat>
      <SummaryStat><span className="label">Min / Max</span><span className="val">{formatLatency(summary.min_latency_ms)} / {formatLatency(summary.max_latency_ms)}</span></SummaryStat>
      <SummaryStat><span className="label">Total</span><span className="val">{formatLatency(summary.total_latency_ms)}</span></SummaryStat>
    </SummaryCol>
  );
};

const renderField = (label, value) => (
  <div>
    <FieldLabel>{label}</FieldLabel>
    {value === null || value === undefined || value === "" ? (
      <NoVal>—</NoVal>
    ) : (
      <FieldValue>{value}</FieldValue>
    )}
  </div>
);

const renderTopQuote = (tq) => {
  if (!tq || (!tq.student && !tq.quote && (!tq.tags || tq.tags.length === 0))) {
    return (
      <div>
        <FieldLabel>Top quote</FieldLabel>
        <NoVal>—</NoVal>
      </div>
    );
  }
  return (
    <div>
      <FieldLabel>Top quote</FieldLabel>
      <FieldValue>
        {tq.student && <div style={{ fontFamily: '"SF Mono", Menlo, monospace', fontSize: 12, color: "#6b7280" }}>Student {tq.student}</div>}
        {tq.quote && <div>{tq.quote}</div>}
        {tq.tags && tq.tags.length > 0 && (
          <div style={{ marginTop: 4 }}>
            {tq.tags.map((t, i) => <TagChip key={`${t}-${i}`}>{t}</TagChip>)}
          </div>
        )}
      </FieldValue>
    </div>
  );
};

const renderAdditional = (additional) => {
  if (!additional || Object.keys(additional).length === 0) return null;
  return (
    <AdditionalDetails>
      <summary>Additional fields ({Object.keys(additional).length})</summary>
      <AdditionalBody>{JSON.stringify(additional, null, 2)}</AdditionalBody>
    </AdditionalDetails>
  );
};

const renderColumn = (label, color, output) => (
  <Column>
    <ColumnTitle color={color}>{label}</ColumnTitle>
    {!output ? (
      <NoVal>No output</NoVal>
    ) : (
      <>
        <div>
          <FieldLabel>Collaboration level</FieldLabel>
          {output.collaboration_level ? (
            <div><PillBadge>{output.collaboration_level}</PillBadge></div>
          ) : (
            <NoVal>—</NoVal>
          )}
        </div>
        {renderField("Level rationale", output.level_rationale)}
        {renderField("Student-facing feedback", output.student_facing_feedback)}
        {renderField("Collaboration tip", output.collaboration_tip)}
        {renderTopQuote(output.top_quote)}
        {(output.tag_tally_scratchpad || output.rubric_gate_check) && (
          <AdditionalDetails>
            <summary>Scratchpad / Rubric gate</summary>
            <AdditionalBody>
              {output.tag_tally_scratchpad && `Tag tally scratchpad:\n${output.tag_tally_scratchpad}\n\n`}
              {output.rubric_gate_check && `Rubric gate check:\n${output.rubric_gate_check}`}
            </AdditionalBody>
          </AdditionalDetails>
        )}
        {renderAdditional(output.additional_fields)}
      </>
    )}
  </Column>
);

export const StudentFeedbackExperimentsComparison = () => {
  const [datasets, setDatasets] = useState([]);
  const [selectedDataset, setSelectedDataset] = useState("");
  const [runs, setRuns] = useState([]);
  const [runA, setRunA] = useState("");
  const [runB, setRunB] = useState("");

  const [loadingDatasets, setLoadingDatasets] = useState(false);
  const [loadingRuns, setLoadingRuns] = useState(false);
  const [loadingComparison, setLoadingComparison] = useState(false);
  const [error, setError] = useState(null);
  const [comparison, setComparison] = useState(null);

  useEffect(() => {
    setLoadingDatasets(true);
    setError(null);
    request("admin/experiments/datasets", { method: "GET" })
      .then((res) => {
        const all = res?.datasets || [];
        setDatasets(all.filter((d) => (d?.name || "").startsWith(DATASET_PREFIX)));
      })
      .catch((e) => {
        logError("Load Langfuse datasets failed", e, { category: LogCategory.ERROR });
        setError(e?.detail || e?.message || "Failed to load datasets");
      })
      .finally(() => setLoadingDatasets(false));
  }, []);

  useEffect(() => {
    if (!selectedDataset) {
      setRuns([]);
      setRunA("");
      setRunB("");
      return;
    }
    setLoadingRuns(true);
    setError(null);
    setRunA("");
    setRunB("");
    request(`admin/experiments/datasets/${encodeURIComponent(selectedDataset)}/runs`, {
      method: "GET",
    })
      .then((res) => {
        setRuns(res?.runs || []);
      })
      .catch((e) => {
        logError("Load Langfuse dataset runs failed", e, { category: LogCategory.ERROR });
        setError(e?.detail || e?.message || "Failed to load runs");
      })
      .finally(() => setLoadingRuns(false));
  }, [selectedDataset]);

  const handleCompare = async () => {
    if (!selectedDataset || !runA || !runB) return;
    if (runA === runB) {
      setError("Pick two different runs.");
      return;
    }
    setLoadingComparison(true);
    setError(null);
    setComparison(null);
    try {
      const res = await request("admin/experiments/compare/student_feedback", {
        method: "POST",
        payload: { dataset_name: selectedDataset, run_a: runA, run_b: runB },
      });
      setComparison(res);
    } catch (e) {
      logError("Compare student feedback experiments failed", e, { category: LogCategory.ERROR });
      setError(e?.detail || e?.message || "Failed to compare experiments");
    } finally {
      setLoadingComparison(false);
    }
  };

  const runOptions = useMemo(
    () =>
      runs.map((r) => (
        <option key={r.id || r.name} value={r.name}>
          {r.name}
        </option>
      )),
    [runs]
  );

  return (
    <Page>
      <h2 style={{ margin: 0 }}>Student Feedback Experiments Comparison</h2>
      <div style={{ color: colors.milkGlass600 || "#6b7280", fontSize: 14 }}>
        Compare two Langfuse Student Feedback dataset runs side-by-side.
      </div>

      <Controls>
        <ControlField>
          <Form.Label style={{ fontWeight: 600 }}>
            Dataset <span style={{ marginLeft: 6, fontSize: 10, color: "#6b7280" }}>▼</span>
          </Form.Label>
          <Form.Control
            as="select"
            value={selectedDataset}
            onChange={(e) => setSelectedDataset(e.target.value)}
            disabled={loadingDatasets}
          >
            <option value="">
              {loadingDatasets ? "Loading datasets…" : "Select a dataset"}
            </option>
            {datasets.map((d) => (
              <option key={d.id} value={d.name}>
                {d.name}
              </option>
            ))}
          </Form.Control>
        </ControlField>

        <ControlField>
          <Form.Label style={{ fontWeight: 600, color: RUN_A_COLOR }}>
            Run A <span style={{ marginLeft: 6, fontSize: 10 }}>▼</span>
          </Form.Label>
          <Form.Control
            as="select"
            value={runA}
            onChange={(e) => setRunA(e.target.value)}
            disabled={!selectedDataset || loadingRuns}
          >
            <option value="">
              {loadingRuns ? "Loading runs…" : "Select run A"}
            </option>
            {runOptions}
          </Form.Control>
        </ControlField>

        <ControlField>
          <Form.Label style={{ fontWeight: 600, color: RUN_B_COLOR }}>
            Run B <span style={{ marginLeft: 6, fontSize: 10 }}>▼</span>
          </Form.Label>
          <Form.Control
            as="select"
            value={runB}
            onChange={(e) => setRunB(e.target.value)}
            disabled={!selectedDataset || loadingRuns}
          >
            <option value="">
              {loadingRuns ? "Loading runs…" : "Select run B"}
            </option>
            {runOptions}
          </Form.Control>
        </ControlField>

        <div style={{ display: "flex", alignItems: "flex-end" }}>
          <Button
            onClick={handleCompare}
            disabled={
              !selectedDataset ||
              !runA ||
              !runB ||
              loadingComparison ||
              loadingRuns
            }
          >
            {loadingComparison ? (
              <>
                <Spinner size="sm" animation="border" /> Comparing…
              </>
            ) : (
              "Compare"
            )}
          </Button>
        </div>
      </Controls>

      {error && <Alert variant="danger">{error}</Alert>}

      {comparison && (
        <>
          {(comparison.run_a_summary || comparison.run_b_summary) && (
            <SummaryRow>
              {renderSummary(shortRunLabel(comparison.run_a), RUN_A_COLOR, comparison.run_a_summary)}
              {renderSummary(shortRunLabel(comparison.run_b), RUN_B_COLOR, comparison.run_b_summary)}
            </SummaryRow>
          )}

          <div style={{ fontSize: 14, color: colors.milkGlass600 || "#6b7280" }}>
            {comparison.items.length} item(s) compared
          </div>

          {comparison.items.map((item, idx) => (
            <ItemCard key={item.dataset_item_id}>
              <ItemHeader>
                <div>
                  <ItemIndex>#{idx + 1}</ItemIndex>{" "}
                  <ItemId>{item.dataset_item_id}</ItemId>
                </div>
                <div style={{ fontSize: 12, color: "#6b7280" }}>
                  <span style={{ color: RUN_A_COLOR, fontWeight: 600 }}>{shortRunLabel(comparison.run_a)}</span>{" "}
                  {formatLatency(item.run_a_latency_ms)}
                  {"  ·  "}
                  <span style={{ color: RUN_B_COLOR, fontWeight: 600 }}>{shortRunLabel(comparison.run_b)}</span>{" "}
                  {formatLatency(item.run_b_latency_ms)}
                </div>
              </ItemHeader>

              <InputDetails>
                <summary>Session metadata + backend calculations + transcript</summary>
                <InputBody>
                  {item.session_metadata && `<Session_metadata>\n${item.session_metadata}\n</Session_metadata>\n\n`}
                  {item.session_backend_calculations && `<Backend_Calculations>\n${item.session_backend_calculations}\n</Backend_Calculations>\n\n`}
                  {item.session_transcript && `<Session_Transcript>\n${item.session_transcript}\n</Session_Transcript>`}
                  {!item.session_metadata && !item.session_backend_calculations && !item.session_transcript && "No input captured."}
                </InputBody>
              </InputDetails>

              <ColumnsGrid>
                {renderColumn("Expected", EXPECTED_COLOR, item.expected)}
                {renderColumn(shortRunLabel(comparison.run_a) || "Run A", RUN_A_COLOR, item.run_a)}
                {renderColumn(shortRunLabel(comparison.run_b) || "Run B", RUN_B_COLOR, item.run_b)}
              </ColumnsGrid>
            </ItemCard>
          ))}
        </>
      )}
    </Page>
  );
};
